// frontend/src/actions/productActions.js

import api from "../utils/axiosInstance";
import * as P from "../constants/productConstants";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";

const API_BASE = "/products";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function toSafeStatus(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  );
}

function isObjectId(value = "") {
  return /^[a-f\d]{24}$/i.test(safeText(value));
}

function encodePathSegment(value) {
  return encodeURIComponent(safeText(value));
}

function containsUnsafeCustomerStockCopy(value = "") {
  const text = normalizeLower(value);

  if (!text) return false;

  return (
    text.includes("reserved") ||
    text.includes("temporarily reserved") ||
    text.includes("allocated") ||
    text.includes("held") ||
    text.includes("held for")
  );
}

function containsSensitiveCustomerCopy(value = "") {
  const text = normalizeLower(value);

  return (
    text.includes("secret") ||
    text.includes("token") ||
    text.includes("authorization") ||
    text.includes("cookie") ||
    text.includes("jwt") ||
    text.includes("mongo") ||
    text.includes("api key") ||
    text.includes("client_secret") ||
    text.includes("access_token") ||
    text.includes("refresh_token")
  );
}

function looksLikeRawProviderCode(value = "") {
  const text = safeText(value);

  return Boolean(text && /^[A-Z0-9_.:-]{8,}$/.test(text));
}

function customerSafeText(value = "", fallback = "") {
  const text = safeText(value);

  if (!text) return safeText(fallback);
  if (containsSensitiveCustomerCopy(text)) return safeText(fallback);
  if (looksLikeRawProviderCode(text)) return safeText(fallback);

  return containsUnsafeCustomerStockCopy(text) ? safeText(fallback) : text;
}

function fallbackMessageForProductIssue({ code = "", status = "" } = {}) {
  const safeCode = normalizeUpper(code);
  const safeStatus = normalizeLower(status);

  if (safeCode === "MAX_IN_CART" || safeStatus === "max_in_cart") {
    return "This option is already in your bag.";
  }

  if (
    safeCode === "SHARED_STOCK_EXHAUSTED_IN_CART" ||
    safeStatus === "shared_stock_exhausted_in_cart"
  ) {
    return "This cut is already in your bag.";
  }

  if (
    safeCode === "SHARED_STOCK_BLOCKED_BY_CART" ||
    safeCode === "INSUFFICIENT_SHARED_STOCK" ||
    safeStatus === "shared_stock_blocked_by_cart"
  ) {
    return "Choose another cut from this shared roll.";
  }

  if (
    safeCode === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    safeCode === "INSUFFICIENT_STOCK" ||
    safeStatus === "requested_qty_exceeds_available" ||
    safeStatus === "insufficient_stock"
  ) {
    return "The selected quantity is no longer available.";
  }

  if (
    safeCode === "OUT_OF_STOCK" ||
    safeStatus === "out_of_stock" ||
    safeStatus === "sold_out"
  ) {
    return "This option is sold out.";
  }

  if (
    safeCode === "UNAVAILABLE" ||
    safeStatus === "unavailable" ||
    safeStatus === "reserved"
  ) {
    return "This option is currently unavailable.";
  }

  return "";
}

function normalizeIssue(issue = {}) {
  const code = normalizeUpper(issue?.code);
  const status = normalizeLower(issue?.stockStatus || issue?.status);
  const fallback = fallbackMessageForProductIssue({ code, status });

  const message = customerSafeText(
    issue?.customerMessage ||
      issue?.message ||
      issue?.statusReason ||
      issue?.stockStatusReason ||
      issue?.disabledReason ||
      issue?.unavailableReason,
    fallback,
  );

  return {
    ...issue,
    code,
    status,
    stockStatus: status,
    message,
    statusReason: message,
    stockStatusReason: message,
    disabledReason: message,
    unavailableReason: message,
    productId: safeText(issue?.productId),
    variantSku: normalizeUpper(issue?.variantSku),
    variantType: normalizeLower(issue?.variantType),
  };
}

function buildErrorPayload(error, fallback = USER_FACING_FALLBACK) {
  const status = toSafeStatus(
    error?.response?.status || error?.statusCode || error?.status || 0,
    0,
  );

  const data = error?.response?.data || {};

  const issues = (
    Array.isArray(data?.issues)
      ? data.issues
      : Array.isArray(error?.issues)
        ? error.issues
        : []
  ).map(normalizeIssue);

  const firstIssue = issues[0] || {};

  const code = normalizeUpper(data?.code || error?.code || error?.errorCode);
  const stockStatus = normalizeLower(
    data?.stockStatus ||
      data?.status ||
      error?.stockStatus ||
      error?.status ||
      firstIssue?.stockStatus,
  );

  const fallbackMessage =
    firstIssue?.message ||
    fallbackMessageForProductIssue({
      code,
      status: stockStatus,
    }) ||
    getApiErrorMessage(error, { fallback });

  const message = customerSafeText(
    data?.customerMessage ||
      data?.message ||
      error?.normalizedMessage ||
      firstIssue?.message,
    fallbackMessage,
  );

  return {
    message: message || getApiErrorMessage(error, { fallback }),
    code,
    status,
    stockStatus,
    issues,
    requestId: safeText(
      data?.requestId ||
        error?.requestId ||
        error?.response?.headers?.["x-request-id"] ||
        "",
    ),
    raw: {
      code,
      status,
      requestId: safeText(
        data?.requestId ||
          error?.requestId ||
          error?.response?.headers?.["x-request-id"] ||
          "",
      ),
    },
  };
}

function assertIdPresent(id) {
  const safeId = safeText(id);

  if (!safeId) {
    throw Object.assign(new Error("Select a product before continuing."), {
      message: "Select a product before continuing.",
      code: "MISSING_ID",
      status: 400,
      requestId: "",
    });
  }

  return safeId;
}

function unwrapProductResponse(data) {
  return data?.product || data;
}

function buildResolveRequestKey(id, params = {}) {
  const variantSku = normalizeUpper(params?.variantSku);
  const variantType = normalizeLower(params?.variantType);
  const qty = safeText(params?.qty ?? "");

  return [safeText(id), variantSku, variantType, qty].join("::");
}

/* -------------------------------------------------------------------------- */
/*                              Request runner                                */
/* -------------------------------------------------------------------------- */

const runRequest =
  ({
    requestType,
    successType,
    failType,
    method,
    urlBuilder,
    bodyBuilder,
    configBuilder,
    successPayloadBuilder,
    requestMetaBuilder,
    successMetaBuilder,
    fallbackErrorMessage = USER_FACING_FALLBACK,
  }) =>
  (...args) =>
  async (dispatch, getState) => {
    const requestMeta =
      typeof requestMetaBuilder === "function"
        ? requestMetaBuilder(...args)
        : undefined;

    try {
      dispatch({
        type: requestType,
        ...(requestMeta ? { meta: requestMeta } : {}),
      });

      const url = urlBuilder(...args);

      const body =
        typeof bodyBuilder === "function" ? bodyBuilder(...args) : undefined;

      const config =
        typeof configBuilder === "function"
          ? configBuilder(getState, ...args)
          : {};

      let response;

      switch (method) {
        case "get":
          response = await api.get(url, config);
          break;

        case "post":
          response = await api.post(url, body, config);
          break;

        case "put":
          response = await api.put(url, body, config);
          break;

        case "patch":
          response = await api.patch(url, body, config);
          break;

        case "delete":
          response = await api.delete(url, config);
          break;

        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      const payload =
        typeof successPayloadBuilder === "function"
          ? successPayloadBuilder(response.data, ...args)
          : response.data;

      const successMeta =
        typeof successMetaBuilder === "function"
          ? successMetaBuilder(response.data, ...args)
          : undefined;

      const mergedMeta =
        requestMeta || successMeta
          ? { ...(requestMeta || {}), ...(successMeta || {}) }
          : undefined;

      dispatch({
        type: successType,
        payload,
        ...(mergedMeta ? { meta: mergedMeta } : {}),
      });

      return payload;
    } catch (error) {
      const payload =
        error?.status && error?.message
          ? buildErrorPayload(error, fallbackErrorMessage)
          : buildErrorPayload(error, fallbackErrorMessage);

      dispatch({
        type: failType,
        payload,
        ...(requestMeta ? { meta: requestMeta } : {}),
      });

      throw payload;
    }
  };

/* -------------------------------------------------------------------------- */
/*                                   Public                                   */
/* -------------------------------------------------------------------------- */

export const listProducts = (params = {}) =>
  runRequest({
    requestType: P.PRODUCT_LIST_REQUEST,
    successType: P.PRODUCT_LIST_SUCCESS,
    failType: P.PRODUCT_LIST_FAIL,
    method: "get",
    urlBuilder: () => API_BASE,
    configBuilder: () => ({ params: cleanParams(params) }),
    fallbackErrorMessage:
      "We could not load products right now. Please try again later.",
  })(params);

export const listLatestProducts = (params = {}) =>
  runRequest({
    requestType: P.PRODUCT_LATEST_REQUEST,
    successType: P.PRODUCT_LATEST_SUCCESS,
    failType: P.PRODUCT_LATEST_FAIL,
    method: "get",
    urlBuilder: () => `${API_BASE}/latest`,
    configBuilder: () => ({
      params: cleanParams({ limit: 4, ...cleanParams(params) }),
    }),
    fallbackErrorMessage:
      "We could not load the latest products right now. Please try again later.",
  })(params);

export const listFeaturedProducts = (params = {}) =>
  runRequest({
    requestType: P.PRODUCT_FEATURED_REQUEST,
    successType: P.PRODUCT_FEATURED_SUCCESS,
    failType: P.PRODUCT_FEATURED_FAIL,
    method: "get",
    urlBuilder: () => API_BASE,
    configBuilder: () => ({
      params: cleanParams({
        page: 1,
        limit: 4,
        sort: "featured",
        includeFacets: false,
        ...cleanParams(params),
        isFeatured: true,
      }),
    }),
    fallbackErrorMessage:
      "We could not load featured products right now. Please try again later.",
  })(params);

export const listProductsByShop = (shopKey, params = {}) =>
  runRequest({
    requestType: P.PRODUCT_LIST_REQUEST,
    successType: P.PRODUCT_LIST_SUCCESS,
    failType: P.PRODUCT_LIST_FAIL,
    method: "get",
    urlBuilder: (safeShopKey) =>
      `${API_BASE}/shop/${encodePathSegment(safeShopKey)}`,
    configBuilder: () => ({ params: cleanParams(params) }),
    fallbackErrorMessage:
      "We could not load shop products right now. Please try again later.",
  })(shopKey, params);

export const getProductDetails = (id) => {
  const safeId = assertIdPresent(id);

  return runRequest({
    requestType: P.PRODUCT_DETAILS_REQUEST,
    successType: P.PRODUCT_DETAILS_SUCCESS,
    failType: P.PRODUCT_DETAILS_FAIL,
    method: "get",
    urlBuilder: (productId) => `${API_BASE}/${encodePathSegment(productId)}`,
    successPayloadBuilder: unwrapProductResponse,
    fallbackErrorMessage:
      "We could not load this product right now. Please try again later.",
  })(safeId);
};

export const getProductByShopAndSlug = (shopKey, slug) =>
  runRequest({
    requestType: P.PRODUCT_BY_SLUG_REQUEST,
    successType: P.PRODUCT_BY_SLUG_SUCCESS,
    failType: P.PRODUCT_BY_SLUG_FAIL,
    method: "get",
    urlBuilder: (safeShopKey, safeSlug) =>
      `${API_BASE}/shop/${encodePathSegment(safeShopKey)}/${encodePathSegment(
        safeSlug,
      )}`,
    successPayloadBuilder: unwrapProductResponse,
    fallbackErrorMessage:
      "We could not load this product right now. Please try again later.",
  })(shopKey, slug);

export const getProductByIdentifier =
  (shopKey, identifier) => async (dispatch) => {
    const safeShopKey = safeText(shopKey);
    const safeIdentifier = safeText(identifier);

    if (!safeShopKey || !safeIdentifier) return null;

    const identifierLooksLikeObjectId = isObjectId(safeIdentifier);

    try {
      const slugResult = await dispatch(
        getProductByShopAndSlug(safeShopKey, safeIdentifier),
      );

      if (slugResult?._id || slugResult?.id) return slugResult;
    } catch (slugError) {
      const slugStatus = toSafeStatus(slugError?.status, 0);

      const canFallbackToId =
        identifierLooksLikeObjectId && [400, 404].includes(slugStatus);

      if (!canFallbackToId) throw slugError;
    }

    if (identifierLooksLikeObjectId) {
      return dispatch(getProductDetails(safeIdentifier));
    }

    return null;
  };

export const resolveProductPurchase = (id, params = {}) => {
  const safeId = assertIdPresent(id);

  return runRequest({
    requestType: P.PRODUCT_RESOLVE_REQUEST,
    successType: P.PRODUCT_RESOLVE_SUCCESS,
    failType: P.PRODUCT_RESOLVE_FAIL,
    method: "get",
    urlBuilder: (productId) =>
      `${API_BASE}/${encodePathSegment(productId)}/purchase`,
    configBuilder: () => ({ params: cleanParams(params) }),
    requestMetaBuilder: (productId, requestParams) => ({
      requestKey: buildResolveRequestKey(productId, requestParams),
    }),
    successMetaBuilder: (_data, productId, requestParams) => ({
      requestKey: buildResolveRequestKey(productId, requestParams),
    }),
    fallbackErrorMessage:
      "We could not load purchase options for this product right now. Please try again later.",
  })(safeId, params);
};

export const listProductRecommendations = (id, params = {}) => {
  const safeId = assertIdPresent(id);

  return runRequest({
    requestType: P.PRODUCT_RECOMMENDATIONS_REQUEST,
    successType: P.PRODUCT_RECOMMENDATIONS_SUCCESS,
    failType: P.PRODUCT_RECOMMENDATIONS_FAIL,
    method: "get",
    urlBuilder: (productId) =>
      `${API_BASE}/${encodePathSegment(productId)}/recommendations`,
    configBuilder: () => ({ params: cleanParams(params) }),
    requestMetaBuilder: (productId) => ({
      productId,
    }),
    successMetaBuilder: (_data, productId) => ({
      productId,
    }),
    fallbackErrorMessage:
      "We could not load recommended products right now. Please try again later.",
  })(safeId, params);
};

/* -------------------------------------------------------------------------- */
/*                                    Reset                                   */
/* -------------------------------------------------------------------------- */

export const resetProductList = () => ({
  type: P.PRODUCT_LIST_RESET,
});

export const resetLatestProducts = () => ({
  type: P.PRODUCT_LATEST_RESET,
});

export const resetProductDetails = () => ({
  type: P.PRODUCT_DETAILS_RESET,
});

export const resetProductBySlug = () => ({
  type: P.PRODUCT_BY_SLUG_RESET,
});

export const resetProductResolve = () => ({
  type: P.PRODUCT_RESOLVE_RESET,
});

export const resetProductRecommendations = () => ({
  type: P.PRODUCT_RECOMMENDATIONS_RESET,
});
